import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { House, MapPin, Package, Lock } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicLocation() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/locations/${token}`)
      .then(({ data }) => setData(data))
      .catch(() => setErr("This label doesn't exist anymore."));
  }, [token]);

  if (err) return (
    <div className="min-h-screen bg-sand-100 grid place-items-center px-6">
      <div className="text-center text-stoneink-700">{err}</div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen bg-sand-100 grid place-items-center">
      <div className="h-10 w-10 rounded-full border-4 border-terracotta-500 border-t-transparent animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-sand-100">
      <header className="backdrop-blur-xl bg-sand-100/80 border-b border-sand-300 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-terracotta-500 text-white grid place-items-center">
            <House weight="duotone" size={20} />
          </div>
          <div>
            <div className="font-heading text-lg font-semibold text-stoneink-900 leading-none">Nestled</div>
            <div className="text-xs text-stoneink-500 mt-0.5">scanned label</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8" data-testid="public-location">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border border-sand-300 bg-white p-8 shadow-ambient">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-2xl bg-sage-400/20 text-sage-600 grid place-items-center shrink-0">
              <MapPin weight="duotone" size={28} />
            </div>
            <div className="flex-1">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-terracotta-600">Inside</div>
              <h1 className="font-heading text-3xl sm:text-4xl text-stoneink-900 font-semibold tracking-tight mt-1">{data.location.name}</h1>
              {data.location.description && <p className="text-stoneink-700 mt-2">{data.location.description}</p>}
              {data.owner_name && <p className="text-stoneink-500 text-sm mt-3">Kept by {data.owner_name}</p>}
            </div>
          </div>
        </motion.div>

        {!data.is_public ? (
          <div className="rounded-3xl border border-dashed border-sand-300 bg-white p-10 text-center">
            <Lock weight="duotone" size={42} className="mx-auto text-stoneink-500" />
            <div className="mt-3 text-stoneink-900 font-medium">This label is private</div>
            <div className="text-stoneink-500 mt-1 text-sm">Ask the owner to enable public viewing.</div>
          </div>
        ) : data.items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-sand-300 bg-white p-10 text-center">
            <Package weight="duotone" size={42} className="mx-auto text-stoneink-500" />
            <div className="mt-3 text-stoneink-700">Nothing stored here yet.</div>
          </div>
        ) : (
          <section>
            <h2 className="font-heading text-2xl text-stoneink-900 font-medium tracking-tight mb-4">
              Contents ({data.items.length})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {data.items.map((it, i) => (
                <motion.div key={it.id}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  className="rounded-3xl border border-sand-300 bg-white p-5 shadow-ambient">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-heading text-lg text-stoneink-900 font-medium">{it.name}</div>
                    {it.quantity > 1 && <span className="text-xs bg-sand-200 text-stoneink-700 rounded-full px-2 py-0.5">×{it.quantity}</span>}
                  </div>
                  {it.description && <p className="text-sm text-stoneink-700 mt-1">{it.description}</p>}
                  {it.tags?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {it.tags.map((t) => <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-sand-200 text-stoneink-700">{t}</span>)}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </section>
        )}

        <footer className="text-center text-stoneink-500 text-sm pt-6">
          Powered by <a href="/" className="text-terracotta-600 font-medium">Nestled</a>
        </footer>
      </main>
    </div>
  );
}
